import { createServerFn } from "@tanstack/react-start";

export interface GlobalCryptoStats {
  total_market_cap: number;
  total_volume_24h: number;
  btc_dominance: number;
  eth_dominance: number;
  market_cap_change_24h: number;
  volume_change_24h: number;
  active_cryptocurrencies: number;
  active_exchanges: number;
  active_market_pairs: number;
  defi_market_cap: number;
  defi_volume_24h: number;
  stablecoin_market_cap: number;
  stablecoin_volume_24h: number;
  last_updated: string;
}

export interface GlobalStatsResult {
  success: boolean;
  data?: GlobalCryptoStats;
  error?: string;
}

/** Public market-wide crypto totals from CoinMarketCap. */
export const fetchGlobalCryptoStats = createServerFn({ method: "GET" }).handler(
  async (): Promise<GlobalStatsResult> => {
    const key = process.env["COINMARKETCAP_API_KEY"];
    if (!key) return { success: false, error: "Market totals are unavailable" };

    try {
      const response = await fetch(
        "https://pro-api.coinmarketcap.com/v1/global-metrics/quotes/latest",
        { headers: { "X-CMC_PRO_API_KEY": key, Accept: "application/json" } },
      );
      if (!response.ok) return { success: false, error: `Market data error ${response.status}` };

      const result = await response.json();
      const g = result?.data;
      if (!g) return { success: false, error: "No market data received" };
      const usd = g.quote?.USD ?? {};

      return {
        success: true,
        data: {
          total_market_cap: usd.total_market_cap ?? 0,
          total_volume_24h: usd.total_volume_24h ?? 0,
          btc_dominance: g.btc_dominance ?? 0,
          eth_dominance: g.eth_dominance ?? 0,
          market_cap_change_24h: usd.total_market_cap_yesterday_percentage_change ?? 0,
          volume_change_24h: usd.total_volume_24h_yesterday_percentage_change ?? 0,
          active_cryptocurrencies: g.active_cryptocurrencies ?? 0,
          active_exchanges: g.active_exchanges ?? 0,
          active_market_pairs: g.active_market_pairs ?? 0,
          defi_market_cap: g.defi_market_cap ?? 0,
          defi_volume_24h: g.defi_volume_24h ?? 0,
          stablecoin_market_cap: g.stablecoin_market_cap ?? 0,
          stablecoin_volume_24h: g.stablecoin_volume_24h ?? 0,
          last_updated: g.last_updated ?? new Date().toISOString(),
        },
      };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  },
);
